import React, { useState, useEffect } from 'react';
import {
  HelpCircle,
  Filter,
  ChevronLeft,
  ChevronRight,
  Eye,
  RefreshCw,
  X,
  Search,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Clock,
  Mail,
  User,
  FileText,
  Calendar,
  AlertCircle as AlertCircleIcon,
  Loader2,
} from 'lucide-react';
import { useAuthenticatedApi } from '../../hooks/useAuthenticatedApi';
import { apiEndpoints } from '../../utils/api';

// Types
type SupportRequestStatus = 'open' | 'resolved' | 'closed';
type SupportRequestCategory = 'bug' | 'ui_issue' | 'performance' | 'feature_request' | 'security' | 'other';

interface SupportRequest {
  id: string;
  ticket_number: string;
  user_id: string;
  email: string;
  name?: string;
  description: string;
  category: SupportRequestCategory;
  status: SupportRequestStatus;
  created_at: string;
  updated_at: string;
  resolution_notes?: string;
  resolution_date?: string;
  sentry_feedback_id?: string;
}

interface ListResponse {
  items: SupportRequest[];
  total: number;
  skip: number;
  limit: number;
}

// Category labels
const categoryLabels: Record<SupportRequestCategory, string> = {
  bug: 'Bug',
  ui_issue: 'UI Issue',
  performance: 'Performance',
  feature_request: 'Feature Request',
  security: 'Security',
  other: 'Other',
};

// Custom scrollbar styles
const customScrollbarStyles = `
  /* WebKit browsers (Chrome, Safari, Edge) */
  .custom-scrollbar::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }

  .custom-scrollbar::-webkit-scrollbar-track {
    background: transparent;
    border-radius: 4px;
  }

  .custom-scrollbar::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.15);
    border-radius: 4px;
  }

  .custom-scrollbar::-webkit-scrollbar-thumb:hover {
    background-color: rgba(0, 0, 0, 0.25);
  }

  .dark .custom-scrollbar::-webkit-scrollbar-thumb {
    background-color: rgba(255, 255, 255, 0.15);
  }

  .dark .custom-scrollbar::-webkit-scrollbar-thumb:hover {
    background-color: rgba(255, 255, 255, 0.25);
  }

  /* Firefox */
  .custom-scrollbar {
    scrollbar-width: thin;
    scrollbar-color: rgba(0, 0, 0, 0.15) transparent;
  }

  .dark .custom-scrollbar {
    scrollbar-color: rgba(255, 255, 255, 0.15) transparent;
  }

  .custom-scrollbar {
    scroll-behavior: smooth;
  }
`;

// Status config
const statusConfig = {
  open: {
    label: 'Open',
    icon: AlertTriangle,
    color: 'text-orange-600 dark:text-orange-400',
    bg: 'bg-orange-100 dark:bg-orange-900/30',
    border: 'border-orange-200 dark:border-orange-800',
  },
  resolved: {
    label: 'Resolved',
    icon: CheckCircle,
    color: 'text-brand-primary',
    bg: 'bg-brand-primary/10',
    border: 'border-brand-primary/20',
  },
  closed: {
    label: 'Closed',
    icon: XCircle,
    color: 'text-gray-600 dark:text-gray-400',
    bg: 'bg-gray-100 dark:bg-gray-900/30',
    border: 'border-gray-200 dark:border-gray-800',
  },
};

const SupportRequestSection: React.FC = () => {
  const api = useAuthenticatedApi();

  // Data state
  const [requests, setRequests] = useState<SupportRequest[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Pagination
  const [skip, setSkip] = useState(0);
  const limit = 20;

  // Filters
  const [statusFilter, setStatusFilter] = useState<SupportRequestStatus | ''>('');
  const [categoryFilter, setCategoryFilter] = useState<SupportRequestCategory | ''>('');
  const [searchQuery, setSearchQuery] = useState('');

  // Modal state
  const [selectedRequest, setSelectedRequest] = useState<SupportRequest | null>(null);
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [updating, setUpdating] = useState(false);
  const [resolutionNotesError, setResolutionNotesError] = useState<string | null>(null);

  // Fetch requests function
  const fetchRequests = async () => {
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({
        skip: skip.toString(),
        limit: limit.toString(),
      });

      if (statusFilter) params.append('status', statusFilter);
      if (categoryFilter) params.append('category', categoryFilter);
      if (searchQuery.trim()) params.append('q', searchQuery.trim());

      const response: ListResponse = await api.get(
        `${apiEndpoints.supportRequests.admin.list}?${params.toString()}`
      );

      setRequests(response.items);
      setTotal(response.total);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch support requests');
    } finally {
      setLoading(false);
    }
  };

  // Fetch on mount and when filters/pagination change
  useEffect(() => {
    fetchRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skip, statusFilter, categoryFilter, searchQuery]);

  // Validate resolution notes
  const validateResolutionNotes = (notes: string): string | null => {
    const trimmed = notes.trim();
    if (trimmed.length === 0) {
      return null; // Empty is allowed
    }
    if (trimmed.length < 10) {
      return 'Resolution notes must be at least 10 characters long';
    }
    return null;
  };

  // Update status
  const handleUpdateStatus = async (status: SupportRequestStatus) => {
    if (!selectedRequest) return;

    // Validate resolution notes if provided
    const trimmedNotes = resolutionNotes.trim();
    const validationError = validateResolutionNotes(resolutionNotes);
    
    if (validationError) {
      setResolutionNotesError(validationError);
      return;
    }

    setUpdating(true);
    setError(null);
    setResolutionNotesError(null);

    try {
      const updated: SupportRequest = await api.patch(
        apiEndpoints.supportRequests.admin.updateStatus(selectedRequest.ticket_number),
        {
          status,
          resolution_notes: trimmedNotes || null,
        }
      );

      setRequests(prev =>
        prev.map(req => (req.ticket_number === selectedRequest.ticket_number ? updated : req))
      );
      setSelectedRequest(updated);
      setResolutionNotes('');
    } catch (err: any) {
      setError(err.message || 'Failed to update status');
    } finally {
      setUpdating(false);
    }
  };

  const filteredRequests = requests;

  // Pagination helpers
  const currentPage = Math.floor(skip / limit) + 1;
  const totalPages = Math.ceil(total / limit);
  const hasNext = skip + limit < total;
  const hasPrev = skip > 0;

  const goToPage = (page: number) => {
    setSkip((page - 1) * limit);
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const clearFilters = () => {
    setStatusFilter('');
    setCategoryFilter('');
    setSearchQuery('');
    setSkip(0);
  };

  const StatusBadge = ({ status }: { status: SupportRequestStatus }) => {
    const config = statusConfig[status];
    const Icon = config.icon;
    return (
      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${config.bg} ${config.color} border ${config.border}`}>
        <Icon className="w-3 h-3 flex-shrink-0" />
        {config.label}
      </span>
    );
  };

  return (
    <>
      <style>{customScrollbarStyles}</style>
      <div className="min-w-0 overflow-x-hidden">
        <div className="p-4 sm:p-6">
          {/* Error Display */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <div className="flex items-center gap-2">
                <AlertCircleIcon className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0" />
                <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
              </div>
            </div>
          )}

          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
            <div className="min-w-0">
              <h2 className="text-xl sm:text-2xl font-semibold text-neutral-900 dark:text-white mb-2">
                User Feedback
              </h2>
              <p className="text-sm text-neutral-600 dark:text-neutral-400">
                {total} total request{total !== 1 ? 's' : ''}
              </p>
            </div>
          </div>

          {/* Filters */}
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-4">
              <Filter className="w-4 h-4 text-neutral-500 dark:text-neutral-400" />
              <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">Filters</span>
            </div>

            <div className="flex flex-col lg:flex-row gap-4">
              <div className="flex flex-col sm:flex-row gap-3 flex-1 min-w-0">
                {/* Search Input */}
                <div className="relative flex-1 min-w-0">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={e => {
                      setSearchQuery(e.target.value);
                      setSkip(0);
                    }}
                    placeholder="Search by ticket #, email, description..."
                    className="w-full pl-9 pr-3 py-2 text-sm bg-white dark:bg-neutral-800 border border-neutral-300 dark:border-neutral-700 rounded-lg focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary text-neutral-900 dark:text-white placeholder-neutral-500"
                  />
                </div>

                {/* Status Filter */}
                <select
                  value={statusFilter}
                  onChange={e => {
                    setStatusFilter(e.target.value as SupportRequestStatus | '');
                    setSkip(0);
                  }}
                  className="flex-1 sm:flex-none sm:w-40 px-3 py-2 text-sm bg-white dark:bg-neutral-800 border border-neutral-300 dark:border-neutral-700 rounded-lg focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary text-neutral-900 dark:text-white"
                >
                  <option value="">All Status</option>
                  {Object.entries(statusConfig).map(([value, config]) => (
                    <option key={value} value={value}>
                      {config.label}
                    </option>
                  ))}
                </select>

                {/* Category Filter */}
                <select
                  value={categoryFilter}
                  onChange={e => {
                    setCategoryFilter(e.target.value as SupportRequestCategory | '');
                    setSkip(0);
                  }}
                  className="flex-1 sm:flex-none sm:w-40 px-3 py-2 text-sm bg-white dark:bg-neutral-800 border border-neutral-300 dark:border-neutral-700 rounded-lg focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary text-neutral-900 dark:text-white"
                >
                  <option value="">All Categories</option>
                  {Object.entries(categoryLabels).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex gap-3 self-end lg:self-auto">
                <button
                  onClick={clearFilters}
                  className="px-4 py-2 text-sm text-brand-primary border border-brand-primary rounded-lg hover:bg-brand-primary/10 dark:hover:bg-brand-primary/20 transition-colors"
                >
                  Clear
                </button>
                <button
                  onClick={fetchRequests}
                  disabled={loading}
                  className="px-4 py-2 bg-brand-primary text-black hover:bg-brand-primary/90 rounded-lg flex items-center justify-center gap-2 text-sm transition-colors disabled:opacity-50"
                >
                  <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                  Refresh
                </button>
              </div>
            </div>
          </div>

          {/* Table Container - Fixed responsive issues */}
          <div className="relative overflow-hidden">
            <div className="bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-xl overflow-hidden">
              {/* Desktop Table - Show only on screens 1200px and above */}
              <div className="hidden min-[1200px]:block">
                <div className="overflow-x-auto custom-scrollbar">
                  <table className="w-full">
                    <thead className="bg-neutral-50 dark:bg-neutral-900">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider whitespace-nowrap">
                          Ticket
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider whitespace-nowrap">
                          User
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider whitespace-nowrap">
                          Category
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider whitespace-nowrap">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider whitespace-nowrap">
                          Created
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider whitespace-nowrap">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="relative bg-white dark:bg-neutral-800 divide-y divide-neutral-200 dark:divide-neutral-700">
                      {/* Loading Overlay for Table Body */}
                      {loading && (
                        <tr>
                          <td colSpan={6} className="relative h-32">
                            <div className="absolute inset-0 bg-white/75 dark:bg-neutral-800/75 backdrop-blur-sm flex items-center justify-center">
                              <div className="flex items-center gap-3 px-4 py-2 bg-white dark:bg-neutral-900 rounded-lg shadow-lg border border-neutral-200 dark:border-neutral-700">
                                <Loader2 className="h-5 w-5 text-brand-primary animate-spin" />
                                <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">Loading...</span>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}

                      {/* Data Rows or Empty State */}
                      {!loading && filteredRequests.length > 0 ? (
                        filteredRequests.map(request => (
                          <tr
                            key={request.id}
                            onClick={() => setSelectedRequest(request)}
                            className="hover:bg-neutral-50 dark:hover:bg-neutral-700/50 transition-colors cursor-pointer"
                          >
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className="text-sm font-mono font-medium text-neutral-900 dark:text-white">
                                {request.ticket_number}
                              </span>
                            </td>
                            <td className="px-6 py-4">
                              <div className="min-w-0">
                                <div className="text-sm text-neutral-900 dark:text-white">
                                  {request.name || ''}
                                </div>
                                <div className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">
                                  {request.email}
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className="text-sm text-neutral-600 dark:text-neutral-400">
                                {categoryLabels[request.category]}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <StatusBadge status={request.status} />
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className="text-sm text-neutral-600 dark:text-neutral-400">
                                {formatDate(request.created_at)}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedRequest(request);
                                }}
                                className="p-2 text-neutral-400 hover:text-brand-primary hover:bg-brand-primary/10 rounded transition-colors"
                                title="View details"
                              >
                                <Eye className="w-5 h-5" />
                              </button>
                            </td>
                          </tr>
                        ))
                      ) : !loading && (
                        <tr>
                          <td colSpan={7} className="px-6 py-12 text-center">
                            <HelpCircle className="mx-auto mb-4 text-neutral-400 dark:text-neutral-500" size={48} />
                            <p className="text-neutral-600 dark:text-neutral-400 mb-2">No feedback found</p>
                            <p className="text-sm text-neutral-500 dark:text-neutral-500">
                              {statusFilter || categoryFilter || searchQuery
                                ? 'Try adjusting your filters or search query'
                                : 'No support requests have been submitted yet'}
                            </p>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Mobile Card Layout - Show below 1200px screens */}
              <div className="min-[1200px]:hidden relative min-h-[200px]">
                {/* Loading Overlay for Mobile */}
                {loading && (
                  <div className="absolute inset-0 bg-white/75 dark:bg-neutral-800/75 backdrop-blur-sm flex items-center justify-center z-10 rounded-xl">
                    <div className="flex items-center gap-3 px-4 py-2 bg-white dark:bg-neutral-900 rounded-lg shadow-lg border border-neutral-200 dark:border-neutral-700">
                      <Loader2 className="h-5 w-5 text-brand-primary animate-spin" />
                      <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">Loading...</span>
                    </div>
                  </div>
                )}

                {/* Mobile Cards or Empty State */}
                {!loading && filteredRequests.length > 0 ? (
                  <div className="divide-y divide-neutral-200 dark:divide-neutral-700">
                    {filteredRequests.map(request => (
                      <div
                        key={request.id}
                        onClick={() => setSelectedRequest(request)}
                        className="p-4 hover:bg-neutral-50 dark:hover:bg-neutral-700/50 transition-colors cursor-pointer"
                      >
                        {/* Header: Ticket & Status */}
                        <div className="flex items-start justify-between gap-2 mb-3">
                          <div className="flex items-center gap-2 min-w-0">
                            <span className="text-sm font-mono font-semibold text-neutral-900 dark:text-white">
                              {request.ticket_number}
                            </span>
                            <StatusBadge status={request.status} />
                          </div>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedRequest(request);
                            }}
                            className="flex-shrink-0 p-2 text-neutral-400 hover:text-brand-primary hover:bg-brand-primary/10 rounded transition-colors"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                        </div>


                        {/* Details Grid */}
                        <div className="space-y-2 text-xs text-neutral-600 dark:text-neutral-400">
                          <div className="flex items-center gap-2">
                            <User className="w-3.5 h-3.5 flex-shrink-0" />
                            <span className="truncate">{request.name || request.email}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <FileText className="w-3.5 h-3.5 flex-shrink-0" />
                            <span>{categoryLabels[request.category]}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Calendar className="w-3.5 h-3.5 flex-shrink-0" />
                            <span>{formatDate(request.created_at)}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : !loading && (
                  <div className="p-8 text-center">
                    <HelpCircle className="mx-auto mb-4 text-neutral-400 dark:text-neutral-500" size={48} />
                    <p className="text-neutral-600 dark:text-neutral-400 mb-2">No feedback found</p>
                    <p className="text-sm text-neutral-500 dark:text-neutral-500">
                      {statusFilter || categoryFilter || searchQuery
                        ? 'Try adjusting your filters or search query'
                        : 'No support requests have been submitted yet'}
                    </p>
                  </div>
                )}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="bg-neutral-50 dark:bg-neutral-900 px-4 sm:px-6 py-4 flex flex-col sm:flex-row items-center justify-between gap-4 border-t border-neutral-200 dark:border-neutral-700">
                  <div className="text-sm text-neutral-700 dark:text-neutral-300 text-center sm:text-left">
                    Showing {skip + 1} to {Math.min(skip + limit, total)} of {total}
                  </div>

                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => goToPage(currentPage - 1)}
                      disabled={!hasPrev}
                      className="p-2 text-neutral-500 hover:text-neutral-700 dark:text-neutral-400 dark:hover:text-neutral-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>

                    <span className="text-sm text-neutral-700 dark:text-neutral-300">
                      Page {currentPage} of {totalPages}
                    </span>

                    <button
                      onClick={() => goToPage(currentPage + 1)}
                      disabled={!hasNext}
                      className="p-2 text-neutral-500 hover:text-neutral-700 dark:text-neutral-400 dark:hover:text-neutral-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Details Modal */}
      {selectedRequest && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-neutral-900 rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col border border-neutral-200 dark:border-neutral-700">
            {/* Modal Header */}
            <div className="flex-none flex items-center justify-between px-6 py-4 border-b border-neutral-200 dark:border-neutral-800">
              <div className="flex items-center gap-3">
                <span className="text-lg font-mono font-semibold text-neutral-900 dark:text-white">
                  {selectedRequest.ticket_number}
                </span>
                <StatusBadge status={selectedRequest.status} />
              </div>
              <button
                onClick={() => {
                  setSelectedRequest(null);
                  setResolutionNotes('');
                  setError(null);
                  setResolutionNotesError(null);
                }}
                className="p-2 text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto p-6 space-y-6 custom-scrollbar">

              {/* Description */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <FileText className="w-4 h-4 text-neutral-500" />
                  <h4 className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase">
                    Description
                  </h4>
                </div>
                <div className="bg-neutral-50 dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-lg p-4">
                  <p className="text-sm text-neutral-900 dark:text-white whitespace-pre-wrap">
                    {selectedRequest.description}
                  </p>
                </div>
              </div>

              {/* Details Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <User className="w-4 h-4 text-neutral-500" />
                    <h4 className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase">
                      Submitted By
                    </h4>
                  </div>
                  {selectedRequest.name && <p className="text-sm text-neutral-900 dark:text-white">{selectedRequest.name}</p>}
                  <div className="flex items-center gap-1.5 mt-1">
                    <Mail className="w-3.5 h-3.5 text-neutral-500" />
                    <p className="text-sm text-neutral-600 dark:text-neutral-400">{selectedRequest.email}</p>
                  </div>
                </div>

                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <FileText className="w-4 h-4 text-neutral-500" />
                    <h4 className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase">
                      Category
                    </h4>
                  </div>
                  <p className="text-sm text-neutral-900 dark:text-white">
                    {categoryLabels[selectedRequest.category]}
                  </p>
                </div>

                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Calendar className="w-4 h-4 text-neutral-500" />
                    <h4 className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase">
                      Created
                    </h4>
                  </div>
                  <p className="text-sm text-neutral-900 dark:text-white">
                    {formatDate(selectedRequest.created_at)}
                  </p>
                </div>

                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Clock className="w-4 h-4 text-neutral-500" />
                    <h4 className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase">
                      Last Updated
                    </h4>
                  </div>
                  <p className="text-sm text-neutral-900 dark:text-white">
                    {formatDate(selectedRequest.updated_at)}
                  </p>
                </div>
              </div>

              {/* Sentry Event ID */}
              {selectedRequest.sentry_feedback_id && (
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <AlertTriangle className="w-4 h-4 text-neutral-500" />
                    <h4 className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase">
                      Sentry Event ID
                    </h4>
                  </div>
                  <code className="text-xs font-mono text-neutral-900 dark:text-white bg-neutral-100 dark:bg-neutral-800 px-2 py-1 rounded">
                    {selectedRequest.sentry_feedback_id}
                  </code>
                </div>
              )}

              {/* Resolution Info */}
              {(selectedRequest.status === 'resolved' || selectedRequest.status === 'closed') && (
                <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-green-900 dark:text-green-100 mb-2">
                    Resolution
                  </h4>
                  {selectedRequest.resolution_date && (
                    <p className="text-sm text-green-800 dark:text-green-200 mb-2">
                      <strong>Date:</strong> {formatDate(selectedRequest.resolution_date)}
                    </p>
                  )}
                  {selectedRequest.resolution_notes ? (
                    <p className="text-sm text-green-800 dark:text-green-200">
                      <strong>Notes:</strong> {selectedRequest.resolution_notes}
                    </p>
                  ) : (
                    <p className="text-sm text-green-700 dark:text-green-300 italic">
                      No resolution notes provided
                    </p>
                  )}
                </div>
              )}

              {/* Update Status Section */}
              {selectedRequest.status === 'open' && (
                <div className="border-t border-neutral-200 dark:border-neutral-800 pt-6">
                  <h4 className="text-sm font-medium text-neutral-900 dark:text-white mb-3">
                    Update Status
                  </h4>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs font-medium text-neutral-700 dark:text-neutral-300 mb-1.5">
                        Resolution Notes (Optional)
                      </label>
                      <textarea
                        value={resolutionNotes}
                        onChange={e => {
                          setResolutionNotes(e.target.value);
                          // Clear error when user starts typing
                          if (resolutionNotesError) {
                            setResolutionNotesError(null);
                          }
                        }}
                        onBlur={() => {
                          // Validate on blur
                          const error = validateResolutionNotes(resolutionNotes);
                          setResolutionNotesError(error);
                        }}
                        placeholder="Add notes about the resolution..."
                        rows={3}
                        className={`w-full px-3 py-2 text-sm bg-white dark:bg-neutral-800 border rounded-lg focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary text-neutral-900 dark:text-white placeholder-neutral-500 resize-none ${
                          resolutionNotesError 
                            ? 'border-red-300 dark:border-red-700' 
                            : 'border-neutral-300 dark:border-neutral-700'
                        }`}
                      />
                      {resolutionNotesError && (
                        <p className="mt-1 text-xs text-red-600 dark:text-red-400">
                          {resolutionNotesError}
                        </p>
                      )}
                      <p className="mt-2 text-xs text-neutral-600 dark:text-neutral-400">
                        Note: Updating the status will send an email notification to the user.
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={() => handleUpdateStatus('resolved')}
                        disabled={updating}
                        className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-black bg-brand-primary hover:bg-brand-primary/90 rounded-lg transition-colors disabled:opacity-50"
                      >
                        <CheckCircle className="w-4 h-4" />
                        Mark as Resolved
                      </button>
                      <button
                        onClick={() => handleUpdateStatus('closed')}
                        disabled={updating}
                        className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-gray-600 hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50"
                      >
                        <XCircle className="w-4 h-4" />
                        Close Request
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SupportRequestSection;
